/**
 * @component ry-format-number
 * @category  display
 * @summary   Formats a number using the specified locale and options. Thin wrapper around the platform `Intl.NumberFormat` API. Pure display — no events, no methods. Set `value` and (optionally) `type`, `currency`, etc.; the element renders the formatted string as its text content and re-renders whenever any attribute changes.
 *
 * Ported verbatim from Shoelace's `<sl-format-number>` (MIT-licensed).
 * Upstream: https://github.com/shoelace-style/shoelace/blob/next/src/components/format-number/format-number.component.ts
 * Docs:     https://shoelace.style/components/format-number
 * License:  MIT — see LICENSE-THIRD-PARTY.md for attribution.
 *
 * Whereas Shoelace's implementation extends ShoelaceElement / LitElement and
 * pulls in a LocalizeController, ry-ui's port lives on top of `RyElement` and
 * calls `Intl.NumberFormat` directly. The visible behavior — attribute names,
 * defaults, rendered text — is identical.
 *
 * @attribute value                         number    [default=0]      The number to format. Any non-numeric / NaN value renders empty.
 * @attribute type                          'currency' | 'decimal' | 'percent'  [default='decimal']  The formatting style to use.
 * @attribute no-grouping                   boolean   [default=false]  Turns off grouping separators (the thousands separator for the active locale).
 * @attribute currency                      string    [default='USD']  The ISO 4217 currency code to use when `type="currency"`.
 * @attribute currency-display              'symbol' | 'narrowSymbol' | 'code' | 'name'  [default='symbol']  How to display the currency.
 * @attribute minimum-integer-digits        number?                    The minimum number of integer digits to use (1-21).
 * @attribute minimum-fraction-digits       number?                    The minimum number of fraction digits to use (0-20).
 * @attribute maximum-fraction-digits       number?                    The maximum number of fraction digits to use (0-20).
 * @attribute minimum-significant-digits    number?                    The minimum number of significant digits to use (1-21).
 * @attribute maximum-significant-digits    number?                    The maximum number of significant digits to use (1-21).
 * @attribute lang                          string?                    BCP 47 locale tag (e.g. `de-DE`, `fr`, `ja-JP`). When omitted, the element inherits the document's lang.
 *
 * @content   None — `<ry-format-number>` is configured entirely via attributes; its innerHTML is replaced at setup and on every attribute change.
 *
 * @gotcha   Re-renders synchronously on any observed attribute change — there is no debounce. Mutating many attributes in a row triggers one render per mutation; batch via `Object.assign(el, {...})` if that matters.
 *
 * @gotcha   `no-grouping` is a boolean attribute (presence-based). The string value is ignored: `no-grouping=""`, `no-grouping="false"`, and `no-grouping="anything"` all disable grouping. Omit the attribute to keep grouping on.
 *
 * @gotcha   `value="abc"` (any non-numeric) renders the empty string, matching upstream Shoelace. The element does NOT throw.
 *
 * @example basic-decimal
 * ```html
 * <ry-format-number value="1000"></ry-format-number>
 * ```
 *
 * @example currency-usd
 * ```html
 * <ry-format-number type="currency" currency="USD" value="2500"></ry-format-number>
 * ```
 *
 * @example currency-eur-name
 * ```html
 * <ry-format-number
 *   type="currency"
 *   currency="EUR"
 *   currency-display="name"
 *   value="1999.95"
 *   lang="en-IE"
 * ></ry-format-number>
 * ```
 *
 * @example percent
 * ```html
 * <ry-format-number type="percent" value="0.42"></ry-format-number>
 * ```
 *
 * @example no-grouping
 * ```html
 * <ry-format-number value="1000000" no-grouping></ry-format-number>
 * ```
 *
 * @example german-locale
 * ```html
 * <ry-format-number value="1234.56" lang="de-DE"></ry-format-number>
 * ```
 *
 * @tutorial decimal-default
 * ```js
 * // Default type is decimal; default value is 0. Grouping separators apply per
 * // the active (en-US fallback) locale.
 * const el = mount('<ry-format-number value="1000"></ry-format-number>');
 * await expectEqual(el.textContent, '1,000', 'en-US grouping renders comma');
 * ```
 *
 * @tutorial currency-usd
 * ```js
 * const el = mount('<ry-format-number type="currency" currency="USD" value="2500"></ry-format-number>');
 * // Symbol display + en-US locale → $2,500.00
 * await expectEqual(el.textContent.includes('$'), true, 'currency symbol rendered');
 * await expectEqual(el.textContent.includes('2,500'), true, 'integer part with grouping');
 * ```
 *
 * @tutorial percent-multiplies-by-100
 * ```js
 * // Intl.NumberFormat percent style multiplies the value by 100 — a common
 * // wrong-assumption trap. value="0.42" renders "42%", NOT "0.42%".
 * const el = mount('<ry-format-number type="percent" value="0.42"></ry-format-number>');
 * await expectEqual(el.textContent.includes('42'), true, 'percent renders 42, not 0.42');
 * await expectEqual(el.textContent.includes('%'), true, 'percent sign present');
 * ```
 *
 * @tutorial no-grouping-removes-separators
 * ```js
 * const grouped   = mount('<ry-format-number value="1000000"></ry-format-number>');
 * const ungrouped = mount('<ry-format-number value="1000000" no-grouping></ry-format-number>');
 * await expectEqual(grouped.textContent,   '1,000,000', 'grouping on by default');
 * await expectEqual(ungrouped.textContent, '1000000',   'no-grouping removes separators');
 * ```
 *
 * @tutorial locale-switch-rerenders
 * ```js
 * // Changing lang re-renders with the new locale's separators.
 * const el = mount('<ry-format-number value="1234.56"></ry-format-number>');
 * await expectEqual(el.textContent, '1,234.56', 'en-US: comma thousands, dot decimal');
 * el.setAttribute('lang', 'de-DE');
 * await expectEqual(el.textContent, '1.234,56', 'de-DE: dot thousands, comma decimal');
 * ```
 *
 * @tutorial invalid-value-renders-empty
 * ```js
 * const el = mount('<ry-format-number value="not-a-number"></ry-format-number>');
 * await expectEqual(el.textContent, '', 'NaN renders empty string');
 * ```
 *
 * @see ry-stat
 */

import { RyElement } from '../core/ry-element.js';

export type RyFormatNumberType = 'currency' | 'decimal' | 'percent';
export type RyFormatNumberCurrencyDisplay = 'symbol' | 'narrowSymbol' | 'code' | 'name';

const OBSERVED = [
  'value',
  'type',
  'no-grouping',
  'currency',
  'currency-display',
  'minimum-integer-digits',
  'minimum-fraction-digits',
  'maximum-fraction-digits',
  'minimum-significant-digits',
  'maximum-significant-digits',
  'lang',
] as const;

export class RyFormatNumber extends RyElement {
  static observedAttributes = OBSERVED;

  setup(): void {
    this.#render();
  }

  attributeChangedCallback(): void {
    // Only render once we're connected (and therefore set up). Pre-connection
    // attribute mutations are picked up by the initial setup() render.
    if (this.isConnected) this.#render();
  }

  // ─── Public typed accessors ───────────────────────────────────────────

  get value(): number {
    const raw = this.getAttribute('value');
    if (raw === null) return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : NaN;
  }
  set value(v: number) { this.setAttribute('value', String(v)); }

  get type(): RyFormatNumberType {
    const raw = this.getAttribute('type');
    return raw === 'currency' || raw === 'percent' ? raw : 'decimal';
  }
  set type(v: RyFormatNumberType) { this.setAttribute('type', v); }

  get noGrouping(): boolean { return this.hasAttribute('no-grouping'); }
  set noGrouping(v: boolean) {
    if (v) this.setAttribute('no-grouping', '');
    else this.removeAttribute('no-grouping');
  }

  get currency(): string { return this.getAttribute('currency') ?? 'USD'; }
  set currency(v: string) { this.setAttribute('currency', v); }

  get currencyDisplay(): RyFormatNumberCurrencyDisplay {
    const raw = this.getAttribute('currency-display');
    return raw === 'narrowSymbol' || raw === 'code' || raw === 'name' ? raw : 'symbol';
  }
  set currencyDisplay(v: RyFormatNumberCurrencyDisplay) { this.setAttribute('currency-display', v); }

  // ─── Render ───────────────────────────────────────────────────────────

  #intAttr(name: string): number | undefined {
    const raw = this.getAttribute(name);
    if (raw === null || raw === '') return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  }

  #render(): void {
    const value = this.value;
    if (!Number.isFinite(value)) {
      this.textContent = '';
      return;
    }

    const opts: Intl.NumberFormatOptions = {
      style: this.type,
      currency: this.currency,
      currencyDisplay: this.currencyDisplay,
      useGrouping: !this.noGrouping,
    };

    const mi  = this.#intAttr('minimum-integer-digits');
    const mnf = this.#intAttr('minimum-fraction-digits');
    const mxf = this.#intAttr('maximum-fraction-digits');
    const mns = this.#intAttr('minimum-significant-digits');
    const mxs = this.#intAttr('maximum-significant-digits');

    if (mi  !== undefined) opts.minimumIntegerDigits     = mi;
    if (mnf !== undefined) opts.minimumFractionDigits    = mnf;
    if (mxf !== undefined) opts.maximumFractionDigits    = mxf;
    if (mns !== undefined) opts.minimumSignificantDigits = mns;
    if (mxs !== undefined) opts.maximumSignificantDigits = mxs;

    const locale = this.getAttribute('lang') || undefined;

    try {
      this.textContent = new Intl.NumberFormat(locale, opts).format(value);
    } catch {
      // Invalid locale / currency / option combo → render empty rather than
      // throw, matching Shoelace's "graceful degrade" behavior.
      this.textContent = '';
    }
  }
}

customElements.define('ry-format-number', RyFormatNumber);
